import safeStorage from '@github-ui/safe-storage'
import {isAgentMode, type AgentMode} from '@github-ui/copilot-shared-ui/utils/agent-mode'

const STORAGE_KEY = 'AGENT_SESSIONS_TASK_MODE_v1'
/**
 * Covers the sessions a user actually revisits without letting the record grow
 * across every task they have ever opened. Least recently recorded tasks fall
 * off the end.
 */
const MAX_ENTRIES = 50

const modeStorage = safeStorage('localStorage', {throwQuotaErrorsOnSet: false})

type StoredEntry = [taskId: string, mode: AgentMode]

function readEntries(): StoredEntry[] {
  try {
    const parsed: unknown = JSON.parse(modeStorage.getItem(STORAGE_KEY) ?? '[]')
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (entry): entry is StoredEntry =>
        Array.isArray(entry) && entry.length === 2 && typeof entry[0] === 'string' && isAgentMode(entry[1]),
    )
  } catch {
    return []
  }
}

/**
 * The mode `taskId` was last known to be in, or `undefined` when nothing has
 * recorded one. Deliberately per task: the agent mode is a property of a
 * session the user returns to, not a preference that should follow them from
 * whatever they last started elsewhere.
 */
export function readTaskAgentMode(taskId: string): AgentMode | undefined {
  return readEntries().find(([id]) => id === taskId)?.[1]
}

/** Records the mode for `taskId`, evicting the oldest entries past the cap. */
export function writeTaskAgentMode(taskId: string, mode: AgentMode): void {
  const next = [[taskId, mode] as StoredEntry, ...readEntries().filter(([id]) => id !== taskId)].slice(0, MAX_ENTRIES)
  modeStorage.setItem(STORAGE_KEY, JSON.stringify(next))
}
